import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  KÉSÉSEK – tanári kezelőfelület.
 *
 *  A napló névsorában rögzített perceket itt lehet áttekinteni és elbírálni. Az órákat
 *  a rendszer a percekből SZÁMOLJA (45 perc = 1 óra, 20/2012. EMMI 51. §), ezért egy
 *  utólagos igazolás azonnal átsorolja az igazolatlanból az igazolt oldalra – nincs
 *  külön mulasztás-rekord, amit vissza kellene keresni.
 *
 *  Az igazoláshoz INDOK kötelező: az átsorolást utólag is meg kell tudni indokolni.
 */
@Component({
  selector: 'app-kesesek',
  templateUrl: './kesesek.component.html',
  styleUrls: ['./kesesek.component.scss'],
})
export class KesesekComponent implements OnInit {

  intezmeny = '';
  /** A belépett dolgozó azonosítója. A szerver a TOKENT részesíti előnyben – ez csak
   *  ott számít, ahol nincs kikényszerítve a tokenes hitelesítés (dev, standalone). */
  allomany = '';
  tanev: number | null = null;
  tanevek: number[] = [];
  admin = false;
  percEgyOra = 45;

  tetelek: any[] = [];
  osszesito: any[] = [];
  toltodik = false;
  hiba = '';

  /* szűrők */
  allapot: 'mind' | 'igazolatlan' | 'igazolt' = 'mind';
  szuro = '';
  tol = '';
  ig = '';

  /* kijelölés + igazolás */
  kijelolt = new Set<string>();
  indok = '';
  mentesFut = false;
  nezet: 'tetelek' | 'osszesito' = 'tetelek';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    const um = await this.data.getFromStorage('userMetadata');
    this.allomany = String(um?.adid || um?.username || um?.preferred_username || '').trim();
    try {
      const t = JSON.parse(await this.data.getFromStorage('tanev'));
      const aktualis = Number(t?.aktualistanev);
      const elso = Number(t?.elsotanev) || aktualis - 3;
      this.tanev = aktualis;
      for (let e = aktualis; e >= elso; e--) this.tanevek.push(e);
    } catch { this.tanev = new Date().getFullYear(); this.tanevek = [this.tanev]; }
    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.toltodik = true;
    this.hiba = '';
    this.kijelolt.clear();
    try {
      const p = new URLSearchParams({ tanev: String(this.tanev), allomany: this.allomany });
      if (this.allapot !== 'mind') p.set('allapot', this.allapot);
      if (this.tol) p.set('tol', this.tol);
      if (this.ig) p.set('ig', this.ig);

      const r: any = await firstValueFrom(this.http.get(`/api/keses/lista/${this.intezmeny}?${p}`));
      this.tetelek = r?.tetelek || [];
      this.admin = !!r?.admin;
      this.percEgyOra = Number(r?.perc_egy_ora) || 45;

      const o: any = await firstValueFrom(this.http.get(
        `/api/keses/osszesito/${this.intezmeny}?tanev=${this.tanev}`));
      this.osszesito = o?.tetelek || [];
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A késések betöltése nem sikerült.';
      this.tetelek = []; this.osszesito = [];
    } finally {
      this.toltodik = false;
    }
  }

  get szurtTetelek(): any[] {
    const k = this.szuro.trim().toLowerCase();
    if (!k) return this.tetelek;
    return this.tetelek.filter((t) =>
      `${t.tanulo_nev} ${t.osztaly_nev} ${t.tantargy_nev || ''}`.toLowerCase().includes(k));
  }

  get szurtOsszesito(): any[] {
    const k = this.szuro.trim().toLowerCase();
    if (!k) return this.osszesito;
    return this.osszesito.filter((t) => `${t.tanulo_nev} ${t.osztaly_nev}`.toLowerCase().includes(k));
  }

  valt(id: string) {
    if (this.kijelolt.has(id)) this.kijelolt.delete(id);
    else this.kijelolt.add(id);
  }

  mind(be: boolean) {
    this.kijelolt = be ? new Set(this.szurtTetelek.map((t) => String(t.id))) : new Set();
  }

  /** Az igazolt óra és a maradék percek – ugyanaz a képlet, mint a szerveren. */
  orak(perc: number): string {
    const p = Number(perc) || 0;
    const ora = Math.floor(p / this.percEgyOra);
    const maradek = p % this.percEgyOra;
    if (!ora) return `${p} perc`;
    return `${ora} óra${maradek ? ` + ${maradek} perc` : ''}`;
  }

  async igazol(igazolt: boolean) {
    const idk = [...this.kijelolt];
    if (!idk.length) return;
    if (igazolt && !this.indok.trim()) {
      this.data.sendToast('Az igazoláshoz add meg az indokot.', 'error');
      return;
    }
    if (!igazolt && !confirm(`${idk.length} késés igazolását visszavonod? `
      + 'Az érintett percek visszakerülnek az igazolatlan oldalra.')) return;

    this.mentesFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/keses/igazolas', {
        idk, igazolt, indok: this.indok.trim(), igazolta: this.allomany,
      }));
      this.data.sendToast(igazolt
        ? `${r.valtozott} késés igazolva.`
        : `${r.valtozott} igazolás visszavonva.`, 'success');
      this.indok = '';
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A művelet nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }

  async torol(t: any) {
    if (!confirm(`Törlöd ${t.tanulo_nev} ${t.perc} perces késését (${String(t.datum).slice(0, 10)})?`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/keses/${t.id}/${this.intezmeny}`));
      this.data.sendToast('A késés törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }
}
